import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
    ModalWrapper,
    ModalRow,
    ButtonWrapper,
    WarningText
} from './styled';

import messages from './messages';

import getMasterCourseKey from './helpers';

import { getDefaultCourse } from '../../containers/course-creator/actions';
import {
    getMasterCourses,
    resetMasterCourses
} from '../../containers/master-courses/actions';

import Modal from '../modal';
import DropdownList from '../dropdown-list';
import Button from '../button';
import Spinner from '../spinner';

/**
 * LINKED COURSE MODAL
 * ---------------------
 * Displayed when user selects 'copy | edit' button from default/3p course table.
 * containers/course-creator initialises modal based off query params.
 * Modal submit handler sets updated master/default courses -> course-creator renders accordingly
 * Key is created to manage both master course id and version
 */
class LinkedCourseModal extends Component {
    constructor(props) {
        super(props);

        this.defaultOptionLength = 100;

        this.state = {
            selectedCurriculumKey: null,
            initialCurriculumKey: null
        };
    }

    static propTypes = {
        getDefaultCourse: PropTypes.func,
        getMasterCourses: PropTypes.func,
        hideModal: PropTypes.func,
        intl: intlShape.isRequired,
        masterCourse: PropTypes.object,
        masterCourseOptions: PropTypes.array,
        match: PropTypes.object.isRequired,
        show: PropTypes.bool,
        selectedGenericCourse: PropTypes.object,
        isLoadingMasterCourses: PropTypes.bool,
        isLoadingGenericCourse: PropTypes.bool,
        resetMasterCourses: PropTypes.func
    };

    async componentDidMount() {
        await this.props.getMasterCourses(null);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            masterCourse,
            masterCourseOptions
        } = prevProps;
        const { selectedCurriculumKey } = prevState;

        const hasLoadedInitialMasterCourse =
            masterCourse.id &&
            masterCourseOptions.length > 0 &&
            !selectedCurriculumKey;

        if (hasLoadedInitialMasterCourse) {
            this.manageInitialData({
                masterCourse,
                masterCourseOptions
            });
        }
    }

    componentWillUnmount() {
        this.props.resetMasterCourses();
    }

    manageInitialData = ({ masterCourseOptions, masterCourse }) => {
        const selectedMasterCourse = masterCourseOptions.find(item => item.key === masterCourse.key);

        if (selectedMasterCourse) {
            this.setState({
                selectedCurriculumKey: selectedMasterCourse.key,
                initialCurriculumKey: selectedMasterCourse.key
            });
        }
    }

    onSubmitHandler = async () => {
        const {
            match: {
                params: {
                    productId,
                    defaultCourseId,
                    version
                }
            },
            masterCourseOptions,
            hideModal
        } = this.props;
        const { selectedCurriculumKey, initialCurriculumKey } = this.state;
        const updatedMasterCourse = masterCourseOptions.find(item => item.key === selectedCurriculumKey);
        const shouldUpdateMasterCourse = selectedCurriculumKey !== initialCurriculumKey;

        if (updatedMasterCourse && shouldUpdateMasterCourse) {
            await this.props.getDefaultCourse(
                productId,
                defaultCourseId,
                version,
                updatedMasterCourse.id,
                updatedMasterCourse.version
            );
            hideModal();
        } else {
            hideModal();
        }
    }

    onDropdownChangeHandler = (key, value) => {
        this.setState({
            selectedCurriculumKey: value
        });
    }

    getWarningMessage = () => {
        const { masterCourseOptions } = this.props;
        const {
            selectedCurriculumKey,
            initialCurriculumKey
        } = this.state;
        const selectedMasterCourse = masterCourseOptions.find(item => item.key === selectedCurriculumKey);
        const initialMasterCourse = masterCourseOptions.find(item => item.key === initialCurriculumKey);

        return `Update master course from ‘${initialMasterCourse.value}’ to ‘${selectedMasterCourse.value}’?`;
    }

    render() {
        const {
            intl,
            selectedGenericCourse,
            masterCourseOptions,
            show,
            isLoadingMasterCourses,
            isLoadingGenericCourse
        } = this.props;
        const {
            selectedCurriculumKey,
            initialCurriculumKey
        } = this.state;
        /**
         Since the functionality to change the master course while copying or editing a course is not permission
         based, there is a potential risk of a team member accidentally changing a master course of a 3P course.
         In order to avoid that we need to introduce a warning message to remind the user of the action.
         */
        const showWarning = selectedCurriculumKey !== initialCurriculumKey;

        // Bits of data coming from everywhere... poorly defined in reducers... massive effort to refactor... apologies
        const shouldRenderContent =
            selectedCurriculumKey &&
            selectedGenericCourse &&
            !isLoadingMasterCourses &&
            !isLoadingGenericCourse;

        return (
            <Modal show={show}>
                <ModalWrapper>
                    {shouldRenderContent ? (
                        <Fragment>
                            <p>{intl.formatMessage(messages.header)}</p>
                            <ModalRow>
                                <DropdownList
                                    onChange={this.onDropdownChangeHandler}
                                    optionLength={this.defaultOptionLength}
                                    options={masterCourseOptions.filter(item =>
                                        item.subjectId === selectedGenericCourse.productId)}
                                    value={selectedCurriculumKey}
                                />
                            </ModalRow>
                            {showWarning && (
                                <ModalRow>
                                    <WarningText>{this.getWarningMessage()}</WarningText>
                                </ModalRow>
                            )}
                            <ModalRow>
                                <ButtonWrapper>
                                    {showWarning && (
                                        <Button
                                            cicle
                                            handleOnClick={() => this.setState({
                                                selectedCurriculumKey: initialCurriculumKey
                                            })}
                                            label={intl.formatMessage(messages.cancel)}
                                        />
                                    )}
                                    <Button
                                        handleOnClick={() => this.onSubmitHandler()}
                                        label={intl.formatMessage(messages.button)}
                                        primary
                                    />
                                </ButtonWrapper>
                            </ModalRow>
                        </Fragment>
                    ) : (
                        <Spinner />
                    )}
                </ModalWrapper>
            </Modal>
        );
    }
}

function mapStateToProps(state) {
    const {
        courseCreator: {
            masterCourse: selectedMasterCourse,
            genericCourse: selectedGenericCourse,
            isFetching: isLoadingGenericCourse
        },
        masterCourse: {
            masterCourses: availableMasterCourses,
            isLoading: isLoadingMasterCourses
        }
    } = state;

    return {
        isLoadingMasterCourses,
        isLoadingGenericCourse,
        masterCourse: {
            ...selectedMasterCourse,
            key: getMasterCourseKey({
                id: selectedMasterCourse.id,
                version: selectedMasterCourse.version
            })
        },
        masterCourseOptions: availableMasterCourses.map(({
            id,
            version,
            name,
            subjectId
        }) => ({
            // We include dropdown lists required values & append version number to display name
            id,
            version,
            value: `${name}-V${version}`,
            key: getMasterCourseKey({
                id,
                version
            }),
            subjectId
        })),
        selectedGenericCourse
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        getDefaultCourse,
        getMasterCourses,
        resetMasterCourses
    }, dispatch);
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(LinkedCourseModal));




// WEBPACK FOOTER //
// ./app/components/linked-course-modal/index.js